import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Mail, Phone, Linkedin, Twitter, Instagram, Send, Globe, ChevronDown, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/company-info`);
        if (res.ok) {
          const data = await res.json();
          setCompanyInfo(data);
          if (data.addresses && data.addresses.length > 0) {
            const primaryAddr = data.addresses.find(a => a.is_primary) || data.addresses[0];
            setSelectedCountry(primaryAddr.country || primaryAddr.label);
          }
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyInfo();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { contactAPI } = await import('../services/api');
      const response = await contactAPI.submitForm(formData);
      toast.success(response.message || 'Thank you! Your message has been sent successfully.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('There was an error submitting your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addresses = companyInfo?.addresses || [];
  const phones = companyInfo?.phones || [];
  const countries = [...new Set(addresses.map(a => a.country || a.label))];
  
  const currentAddress = addresses.find(a => (a.country || a.label) === selectedCountry);
  const currentPhones = phones.filter(p => (p.country || 'India') === selectedCountry);
  const displayPhones = currentPhones.length > 0 ? currentPhones : phones;

  // Generate Google Maps embed URL with single pin marker
  const getMapEmbedUrl = (address) => {
    if (address?.address) {
      // Extract just the key location name + city for cleaner search
      const parts = address.address.split(',');
      // Take the first meaningful part and city
      let query = address.address;
      if (parts.length >= 2) {
        // Try to get landmark + city (e.g., "Prestige Shantiniketan, Bengaluru")
        const landmark = parts[0].replace(/^\d+[,\s]*/, '').trim();
        const city = parts.find(p => 
          p.toLowerCase().includes('bengaluru') || 
          p.toLowerCase().includes('bangalore') ||
          p.toLowerCase().includes('fremont') ||
          p.toLowerCase().includes('california')
        );
        if (city) {
          query = `${landmark}, ${city.trim()}`;
        }
      }
      const encodedAddress = encodeURIComponent(query);
      return `https://maps.google.com/maps?q=${encodedAddress}&z=15&output=embed`;
    }
    return `https://maps.google.com/maps?q=Bangalore,India&z=14&output=embed`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="contact-page">
      <SEO 
        title="Contact Us - Navaris Digital"
        description="Get in touch with Navaris Digital. Let's discuss your project requirements and how we can help transform your business."
        canonicalUrl="/contact"
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-[#320547]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#95228A]/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#95228A]/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <span className="inline-block px-4 py-1.5 bg-white/10 rounded-full text-white/80 text-sm font-medium mb-6">
              Contact Us
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="contact-hero-title">
              Let's Work Together
            </h1>
            <p className="text-white/70 text-lg">
              Have a project in mind? Reach out and let's create something amazing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            
            {/* Left - Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Office Selector */}
              {countries.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-[#320547] uppercase tracking-wider">
                      Our Offices
                    </h3>
                    {countries.length > 1 && (
                      <div className="relative">
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#e5e7eb] rounded-lg text-sm font-medium text-[#320547] hover:border-[#320547]/30 transition-colors"
                          data-testid="country-selector"
                        >
                          <Globe size={14} className="text-[#95228A]" />
                          {selectedCountry}
                          <ChevronDown size={14} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        <AnimatePresence>
                          {isDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                              className="absolute right-0 top-full mt-1 bg-white border border-[#e5e7eb] rounded-lg shadow-lg overflow-hidden z-50 min-w-[140px]"
                            >
                              {countries.map((country, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    setSelectedCountry(country);
                                    setIsDropdownOpen(false);
                                  }}
                                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-[#f8f9fa] transition-colors ${
                                    selectedCountry === country ? 'bg-[#320547]/5 text-[#320547] font-medium' : 'text-[#6b7280]'
                                  }`}
                                  data-testid={`country-option-${country}`}
                                >
                                  {country}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>

                  {/* Address */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedCountry}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="bg-white rounded-2xl p-6 border border-[#e5e7eb] shadow-sm"
                    >
                      {currentAddress && (
                        <div className="flex gap-4">
                          <div className="w-10 h-10 rounded-xl bg-[#320547] flex items-center justify-center flex-shrink-0">
                            <MapPin size={18} className="text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-[#95228A] uppercase tracking-wider mb-1">
                              {currentAddress.label}
                            </p>
                            <p className="text-[#320547] font-medium leading-relaxed">
                              {currentAddress.address}
                            </p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}

              {/* Contact Methods */}
              <div className="space-y-3">
                {/* Phone Numbers */}
                {displayPhones.length > 0 && displayPhones.map((phone, idx) => (
                  <a
                    key={idx}
                    href={`tel:${phone.number}`}
                    className="flex items-center gap-4 p-4 bg-white rounded-xl border border-[#e5e7eb] hover:border-[#95228A]/30 hover:shadow-sm transition-all group"
                    data-testid={`phone-card-${idx}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#95228A]/10 flex items-center justify-center group-hover:bg-[#95228A] transition-colors">
                      <Phone size={18} className="text-[#95228A] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider">{phone.label}</p>
                      <p className="text-[#320547] font-semibold">{phone.number}</p>
                    </div>
                    <ArrowUpRight size={16} className="text-[#95228A] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                ))}

                {/* Email */}
                {companyInfo?.email && (
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center gap-4 p-4 bg-white rounded-xl border border-[#e5e7eb] hover:border-[#95228A]/30 hover:shadow-sm transition-all group"
                    data-testid="email-card"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#320547]/10 flex items-center justify-center group-hover:bg-[#320547] transition-colors">
                      <Mail size={18} className="text-[#320547] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider">Email</p>
                      <p className="text-[#320547] font-semibold">{companyInfo.email}</p>
                    </div>
                    <ArrowUpRight size={16} className="text-[#320547] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                )}
              </div>

              {/* Social Links */}
              {(companyInfo?.linkedin || companyInfo?.twitter || companyInfo?.instagram) && (
                <div>
                  <h3 className="text-sm font-semibold text-[#320547] uppercase tracking-wider mb-4">
                    Follow Us
                  </h3>
                  <div className="flex gap-2">
                    {companyInfo?.linkedin && (
                      <a
                        href={companyInfo.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 rounded-xl bg-white border border-[#e5e7eb] flex items-center justify-center text-[#6b7280] hover:bg-[#0077B5] hover:border-[#0077B5] hover:text-white transition-all"
                        data-testid="social-linkedin"
                      >
                        <Linkedin size={18} />
                      </a>
                    )}
                    {companyInfo?.twitter && (
                      <a
                        href={companyInfo.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 rounded-xl bg-white border border-[#e5e7eb] flex items-center justify-center text-[#6b7280] hover:bg-[#1DA1F2] hover:border-[#1DA1F2] hover:text-white transition-all"
                        data-testid="social-twitter"
                      >
                        <Twitter size={18} />
                      </a>
                    )}
                    {companyInfo?.instagram && (
                      <a
                        href={companyInfo.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 rounded-xl bg-white border border-[#e5e7eb] flex items-center justify-center text-[#6b7280] hover:bg-[#E4405F] hover:border-[#E4405F] hover:text-white transition-all"
                        data-testid="social-instagram"
                      >
                        <Instagram size={18} />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Map */}
              <div className="rounded-2xl overflow-hidden border border-[#e5e7eb] shadow-sm bg-[#f0f0f0]">
                <iframe
                  key={selectedCountry}
                  src={getMapEmbedUrl(currentAddress)}
                  width="100%"
                  height="220"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                  className="w-full"
                />
              </div>
            </div>

            {/* Right - Contact Form */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 md:p-10 border border-[#e5e7eb] shadow-sm"
              >
                <h2 className="text-2xl font-bold text-[#320547] mb-2">
                  Send us a message
                </h2>
                <p className="text-[#6b7280] mb-8">
                  Fill out the form and we'll get back to you within 24 hours.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#320547] mb-2">
                        Full Name <span className="text-[#95228A]">*</span>
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="h-12 bg-[#fafafa] border-[#e5e7eb] focus:border-[#320547] focus:ring-[#320547] rounded-xl"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[#320547] mb-2">
                        Email <span className="text-[#95228A]">*</span>
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className="h-12 bg-[#fafafa] border-[#e5e7eb] focus:border-[#320547] focus:ring-[#320547] rounded-xl"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-[#320547] mb-2">
                        Phone
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="h-12 bg-[#fafafa] border-[#e5e7eb] focus:border-[#320547] focus:ring-[#320547] rounded-xl"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-[#320547] mb-2">
                        Company
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company"
                        className="h-12 bg-[#fafafa] border-[#e5e7eb] focus:border-[#320547] focus:ring-[#320547] rounded-xl"
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-[#320547] mb-2">
                      Message <span className="text-[#95228A]">*</span>
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      className="bg-[#fafafa] border-[#e5e7eb] focus:border-[#320547] focus:ring-[#320547] resize-none rounded-xl"
                      data-testid="contact-message-input"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 bg-[#320547] hover:bg-[#95228A] text-white font-semibold rounded-xl transition-all duration-300"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Send Message
                        <Send size={18} />
                      </span>
                    )}
                  </Button>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
